export const WARRANTY_MODIFIER = {
  VALID: 'valid',
  EXPIRED: 'expired',
  RETURNED: 'returned',
  USED: 'used',
};

/**
 * Evaluates a warranty date and returns badge modifier, display label and date.
 *
 * @param {string|null} dateValue  - ISO date string from API
 * @param {boolean}     isReturned - whether the row is marked as returned
 * @param {{ expiredLabel?: string, returnedLabel?: string, usedLabel?: string, untilLabel?: string }} [translations] - labels for badge text
 * @param {boolean}     [isUsed]   - whether the service for this warranty was already used (e.g. loss and damage)
 * @returns {{ modifier: 'valid'|'expired'|'returned'|'used'|null, displayLabel: string|null, date: Date|null, shortTextMode: 'label'|'date'|null }}
 */
export function evaluateWarrantyDate(dateValue, isReturned, translations = {}, isUsed = false) {
  const warrantyEndDate = dateValue ? new Date(dateValue) : null;
  const hasValidDate =
    warrantyEndDate && !isNaN(warrantyEndDate.getTime()) && warrantyEndDate.getFullYear() >= 1900;

  if (!hasValidDate) {
    return { modifier: null, displayLabel: null, date: null, shortTextMode: null };
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  warrantyEndDate.setHours(0, 0, 0, 0);

  const statuses = {
    [WARRANTY_MODIFIER.RETURNED]: {
      displayLabel: translations.returnedLabel || 'Returned',
      shortTextMode: 'label',
    },
    [WARRANTY_MODIFIER.USED]: {
      displayLabel: translations.usedLabel || 'Used',
      shortTextMode: 'label',
    },
    [WARRANTY_MODIFIER.VALID]: {
      displayLabel: translations.untilLabel || 'Until',
      shortTextMode: 'date',
    },
    [WARRANTY_MODIFIER.EXPIRED]: {
      displayLabel: translations.expiredLabel || 'Expired',
      shortTextMode: 'label',
    },
  };

  let modifier = WARRANTY_MODIFIER.EXPIRED;
  if (isReturned) {
    modifier = WARRANTY_MODIFIER.RETURNED;
  } else if (isUsed) {
    modifier = WARRANTY_MODIFIER.USED;
  } else if (warrantyEndDate >= today) {
    modifier = WARRANTY_MODIFIER.VALID;
  }

  return { modifier, ...statuses[modifier], date: warrantyEndDate };
}
